import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  alpha,
  createTheme,
  useTheme as useMuiTheme,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  Tabs,
  Tab
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Add as AddIcon,
  QrCode as QrCodeIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';
import useSidebar from '../hooks/useSidebar';
import api from '../services/api';
import { buscarMotivosEmissao } from '../services/relatoriosApi';

// Importando componentes refatorados
import ServicosTable from '../components/servicos-senhas/ServicosTable';
import SearchField from '../components/servicos-senhas/SearchField';
import ServicoFormDialog from '../components/servicos-senhas/ServicoFormDialog';
import DeleteConfirmDialog from '../components/servicos-senhas/DeleteConfirmDialog';
import MotivosTable from '../components/servicos-senhas/MotivosTable';
import MotivoFormDialog from '../components/servicos-senhas/MotivoFormDialog';

// Componente para painel de abas
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ServicoSenhas = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { isDarkMode } = useTheme();
  const baseTheme = useMuiTheme();
  const navigate = useNavigate();

  // Estado para controle das abas
  const [tabValue, setTabValue] = useState(0);

  // Estados para gerenciar serviços
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para gerenciar motivos de emissão
  const [motivosEmissao, setMotivosEmissao] = useState([]);
  const [loadingMotivos, setLoadingMotivos] = useState(false);
  const [errorMotivos, setErrorMotivos] = useState(null);
  const [searchTermMotivos, setSearchTermMotivos] = useState('');

  // Estados para ordenação
  const [orderBy, setOrderBy] = useState('nome');
  const [orderDirection, setOrderDirection] = useState('asc');

  // Estados para diálogo de adicionar/editar serviços
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentServico, setCurrentServico] = useState({
    id: null,
    nome: '',
    abreviacao: '',
    descricao: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Estados para diálogo de adicionar/editar motivos
  const [motivoDialogOpen, setMotivoDialogOpen] = useState(false);
  const [isEditingMotivo, setIsEditingMotivo] = useState(false);
  const [currentMotivo, setCurrentMotivo] = useState({
    id: null,
    nome: '',
    descricao: '',
    requer_justificativa: false,
    ativo: true
  });
  const [motivoFormErrors, setMotivoFormErrors] = useState({});
  const [submittingMotivo, setSubmittingMotivo] = useState(false);
  
  // Estado para notificações
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Estado para o modal de confirmação de exclusão
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    servicoId: null,
    servicoNome: ''
  });

  // Estado para o modal de confirmação de exclusão de motivos
  const [deleteConfirmMotivoDialog, setDeleteConfirmMotivoDialog] = useState({
    open: false,
    motivoId: null,
    motivoNome: ''
  });

  // Criando um tema personalizado
  const customTheme = createTheme({
    ...baseTheme,
    typography: {
      fontFamily: [
        'Montserrat',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
    },
  });

  // Função para lidar com mudança de aba
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1 && motivosEmissao.length === 0) {
      fetchMotivosEmissao();
    }
  };

  // Função para buscar serviços da API
  const fetchServicos = async () => {
    setLoading(true);
    try {
      const response = await api.get('/servicos');
      if (response.data.success) {
        setServicos(response.data.data);
      } else {
        setError('Erro ao carregar serviços');
      }
    } catch (err) {
      console.error('Erro ao buscar serviços:', err);
      setError('Falha ao carregar dados dos serviços. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar motivos de emissão da API
  const fetchMotivosEmissao = async () => {
    setLoadingMotivos(true);
    setErrorMotivos(null);
    try {
      const response = await buscarMotivosEmissao();
      if (response.success) {
        // Ordenar motivos para garantir que "Outro" sempre seja o último
        const motivosOrdenados = response.data.sort((a, b) => {
          const aIsOutro = a.nome.toLowerCase() === 'outro';
          const bIsOutro = b.nome.toLowerCase() === 'outro';
          
          if (aIsOutro && !bIsOutro) return 1; // a vai para o final
          if (!aIsOutro && bIsOutro) return -1; // b vai para o final
          if (aIsOutro && bIsOutro) return 0; // ambos são "outro", manter ordem
          
          // Para os demais, ordenar alfabeticamente
          return a.nome.localeCompare(b.nome);
        });
        
        setMotivosEmissao(motivosOrdenados);
      } else {
        setErrorMotivos('Erro ao carregar motivos de emissão');
      }
    } catch (err) {
      console.error('Erro ao buscar motivos de emissão:', err);
      setErrorMotivos('Falha ao carregar dados dos motivos de emissão. Por favor, tente novamente.');
    } finally {
      setLoadingMotivos(false);
    }
  };

  // Buscar serviços ao carregar o componente
  useEffect(() => {
    fetchServicos();
  }, []);

  // Funções para gerenciar motivos de emissão
  const openAddMotivoDialog = () => {
    setIsEditingMotivo(false);
    setCurrentMotivo({
      id: null,
      nome: '',
      descricao: '',
      requer_justificativa: false,
      ativo: true
    });
    setMotivoFormErrors({});
    setMotivoDialogOpen(true);
  };

  const openEditMotivoDialog = (motivo) => {
    setIsEditingMotivo(true);
    setCurrentMotivo({
      id: motivo.id,
      nome: motivo.nome,
      descricao: motivo.descricao || '',
      requer_justificativa: motivo.requer_justificativa || false,
      ativo: motivo.ativo !== false
    });
    setMotivoFormErrors({});
    setMotivoDialogOpen(true);
  };

  const handleCloseMotivoDialog = () => {
    setMotivoDialogOpen(false);
  };

  const handleMotivoInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setCurrentMotivo({
      ...currentMotivo,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Limpar erro do campo que está sendo editado
    if (motivoFormErrors[name]) {
      setMotivoFormErrors({
        ...motivoFormErrors,
        [name]: null
      });
    }
  };

  const validateMotivoForm = () => {
    const errors = {};
    
    if (!currentMotivo.nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    }
    
    // Verificar se o nome já existe (somente para novos motivos ou se o nome mudou)
    const nomeExiste = motivosEmissao.some(
      m => m.nome.toLowerCase() === currentMotivo.nome.toLowerCase() && 
      m.id !== currentMotivo.id
    );
    
    if (nomeExiste) {
      errors.nome = 'Este nome de motivo já está em uso';
    }
    
    setMotivoFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleMotivoSubmit = async () => {
    if (!validateMotivoForm()) return;
    
    setSubmittingMotivo(true);
    
    try {
      let response;
      
      if (isEditingMotivo) {
        // Editar motivo existente
        response = await api.put(`/motivos-emissao/${currentMotivo.id}`, currentMotivo);
      } else {
        // Adicionar novo motivo
        response = await api.post('/motivos-emissao', currentMotivo);
      }
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: isEditingMotivo ? 'Motivo atualizado com sucesso' : 'Motivo adicionado com sucesso',
          severity: 'success'
        });
        
        fetchMotivosEmissao();
        handleCloseMotivoDialog();
      } else {
        setSnackbar({
          open: true,
          message: `Erro: ${response.data.message || 'Falha na operação'}`,
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Erro ao salvar motivo:', err);
      
      if (err.response && err.response.data && err.response.data.message) {
        setSnackbar({
          open: true,
          message: `Erro: ${err.response.data.message}`,
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Erro ao salvar motivo. Verifique sua conexão e tente novamente.',
          severity: 'error'
        });
      }
    } finally {
      setSubmittingMotivo(false);
    }
  };

  const handleDeleteMotivo = async (id) => {
    const motivo = motivosEmissao.find(m => m.id === id);
    if (!motivo) return;
    
    setDeleteConfirmMotivoDialog({
      open: true,
      motivoId: id,
      motivoNome: motivo.nome
    });
  };
  
  const confirmDeleteMotivo = async () => {
    const id = deleteConfirmMotivoDialog.motivoId;
    
    try {
      const response = await api.delete(`/motivos-emissao/${id}`);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Motivo excluído com sucesso',
          severity: 'success'
        });
        
        setDeleteConfirmMotivoDialog({
          open: false,
          motivoId: null,
          motivoNome: ''
        });
        
        fetchMotivosEmissao();
      } else {
        setSnackbar({
          open: true,
          message: `Erro: ${response.data.message || 'Falha ao excluir motivo'}`,
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Erro ao excluir motivo:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir motivo. Verifique sua conexão e tente novamente.',
        severity: 'error'
      });
    }
  };
  
  const cancelDeleteMotivo = () => {
    setDeleteConfirmMotivoDialog({
      open: false,
      motivoId: null,
      motivoNome: ''
    });
  };

  // Função para alterar status ativo/inativo do motivo
  const handleToggleAtivoMotivo = async (motivoId, novoStatus) => {
    try {
      const response = await api.patch(`/motivos-emissao/${motivoId}/toggle-ativo`, {
        ativo: novoStatus
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: 'success'
        });
        
        fetchMotivosEmissao(); // Recarregar a lista
      } else {
        setSnackbar({
          open: true,
          message: `Erro: ${response.data.message}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao alterar status do motivo:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao alterar status do motivo. Tente novamente.',
        severity: 'error'
      });
    }
  };

  // Função para alterar configuração de justificativa do motivo
  const handleToggleJustificativaMotivo = async (motivoId, novoStatus) => {
    try {
      const response = await api.patch(`/motivos-emissao/${motivoId}/toggle-justificativa`, {
        requer_justificativa: novoStatus
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: 'success'
        });
        
        fetchMotivosEmissao(); // Recarregar a lista
      } else {
        setSnackbar({
          open: true,
          message: `Erro: ${response.data.message}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao alterar configuração de justificativa:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao alterar configuração de justificativa. Tente novamente.',
        severity: 'error'
      });
    }
  };

  // Função para lidar com a alteração na ordenação
  const handleSort = (column) => {
    if (orderBy === column) {
      // Se já estamos ordenando por esta coluna, alternar a direção
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Caso contrário, definir a nova coluna e resetar a direção para 'asc'
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  // Funções para manipulação do formulário
  const openAddDialog = () => {
    setIsEditing(false);
    setCurrentServico({
      id: null,
      nome: '',
      abreviacao: '',
      descricao: ''
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (servico) => {
    setIsEditing(true);
    setCurrentServico({
      id: servico.id,
      nome: servico.nome,
      abreviacao: servico.abreviacao,
      descricao: servico.descricao || ''
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setCurrentServico({
      ...currentServico,
      [name]: value
    });
    
    // Limpar erro do campo que está sendo editado
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!currentServico.nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    }
    
    if (!currentServico.abreviacao.trim()) {
      errors.abreviacao = 'Abreviação é obrigatória';
    } else if (currentServico.abreviacao.length > 3) {
      errors.abreviacao = 'Abreviação deve ter no máximo 3 caracteres';
    }
    
    // Verificar se a abreviação já existe (somente para novos serviços ou se a abreviação mudou)
    const abreviacaoExiste = servicos.some(
      s => s.abreviacao.toLowerCase() === currentServico.abreviacao.toLowerCase() && 
      s.id !== currentServico.id
    );
    
    if (abreviacaoExiste) {
      errors.abreviacao = 'Esta abreviação já está em uso';
    }
    
    // Verificar se o nome já existe (somente para novos serviços ou se o nome mudou)
    const nomeExiste = servicos.some(
      s => s.nome.toLowerCase() === currentServico.nome.toLowerCase() && 
      s.id !== currentServico.id
    );
    
    if (nomeExiste) {
      errors.nome = 'Este nome de serviço já está em uso';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    // Adiciona o campo ativo como true por padrão
    const servicoData = {
      ...currentServico,
      ativo: true
    };
    
    try {
      let response;
      
      if (isEditing) {
        // Editar serviço existente
        response = await api.put(`/servicos/${currentServico.id}`, servicoData);
      } else {
        // Adicionar novo serviço
        response = await api.post('/servicos', servicoData);
      }
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: isEditing ? 'Serviço atualizado com sucesso' : 'Serviço adicionado com sucesso',
          severity: 'success'
        });
        
        fetchServicos();
        handleCloseDialog();
      } else {
        setSnackbar({
          open: true,
          message: `Erro: ${response.data.message || 'Falha na operação'}`,
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Erro ao salvar serviço:', err);
      
      // Verificar se o erro é de validação
      if (err.response && err.response.data && err.response.data.message) {
        setSnackbar({
          open: true,
          message: `Erro: ${err.response.data.message}`,
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Erro ao salvar serviço. Verifique sua conexão e tente novamente.',
          severity: 'error'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteServico = async (id) => {
    // Buscar o serviço para mostrar nome na confirmação
    const servico = servicos.find(s => s.id === id);
    if (!servico) return;
    
    // Abrir o modal de confirmação
    setDeleteConfirmDialog({
      open: true,
      servicoId: id,
      servicoNome: servico.nome
    });
  };
  
  const confirmDelete = async () => {
    const id = deleteConfirmDialog.servicoId;
    
    try {
      const response = await api.delete(`/servicos/${id}`);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Serviço excluído com sucesso',
          severity: 'success'
        });
        
        // Fechar o modal
        setDeleteConfirmDialog({
          open: false,
          servicoId: null,
          servicoNome: ''
        });
        
        fetchServicos();
      } else {
        setSnackbar({
          open: true,
          message: `Erro: ${response.data.message || 'Falha ao excluir serviço'}`,
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Erro ao excluir serviço:', err);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir serviço. Verifique sua conexão e tente novamente.',
        severity: 'error'
      });
    }
  };
  
  const cancelDelete = () => {
    setDeleteConfirmDialog({
      open: false,
      servicoId: null,
      servicoNome: ''
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Função para atualizar o status de exibir na emissão
  const handleToggleExibirNaEmissao = async (servicoId, novoStatus) => {
    try {
      const response = await api.patch(`/servicos/${servicoId}`, {
        exibir_na_emissao: novoStatus
      });

      if (response.data.success) {
        // Atualizar o estado local
        setServicos(prevServicos => 
          prevServicos.map(servico => 
            servico.id === servicoId 
              ? { ...servico, exibir_na_emissao: novoStatus }
              : servico
          )
        );

        setSnackbar({
          open: true,
          message: `Serviço ${novoStatus ? 'habilitado' : 'desabilitado'} para emissão de senhas`,
          severity: 'success'
        });
      } else {
        throw new Error(response.data.message || 'Erro ao atualizar serviço');
      }
    } catch (error) {
      console.error('Erro ao atualizar status do serviço:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar status do serviço: ' + (error.response?.data?.message || error.message),
        severity: 'error'
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: alpha(customTheme.palette.primary.main, 0.03) }}>
      <Sidebar
        sidebarOpen={sidebarOpen}
        customTheme={customTheme}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Header
          sidebarOpen={sidebarOpen}
                      handleSidebarToggle={toggleSidebar}
          customTheme={customTheme}
        />

        <Container maxWidth="xxl" sx={{ mt: 10, mb: 4 }}>
          {/* Breadcrumbs para navegação */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{ mb: 3 }}
          >
            <Link 
              color="inherit" 
              href="/dashboard" 
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Dashboard
            </Link>
            <Link 
              color="inherit" 
              href="/configuracoes" 
              onClick={(e) => {
                e.preventDefault();
                navigate('/configuracoes');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Configurações
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <QrCodeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Serviços de Senhas
            </Typography>
          </Breadcrumbs>
          
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
              background: theme => theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(customTheme.palette.grey[900], 0.95)} 0%, ${alpha(customTheme.palette.grey[800], 0.9)} 100%)`
                : 'background.paper',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden'
            }}
          >
            {/* Cabeçalho */}
            <Box sx={{ p: 4, pb: 0 }}>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: 700,
                  background: theme => theme.palette.mode === 'dark'
                    ? `linear-gradient(90deg, ${customTheme.palette.primary.light} 0%, ${customTheme.palette.primary.main} 100%)`
                    : `linear-gradient(90deg, ${customTheme.palette.primary.main} 0%, ${customTheme.palette.primary.dark} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Configuração de Serviços e Motivos
              </Typography>

              {/* Abas */}
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    minHeight: 48,
                    px: 3,
                  },
                  '& .Mui-selected': {
                    color: customTheme.palette.primary.main,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: customTheme.palette.primary.main,
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab
                  icon={<QrCodeIcon />}
                  iconPosition="start"
                  label="Serviços de Senhas"
                  id="simple-tab-0"
                  aria-controls="simple-tabpanel-0"
                />
                <Tab
                  icon={<AssignmentIcon />}
                  iconPosition="start"
                  label="Motivos de Emissão"
                  id="simple-tab-1"
                  aria-controls="simple-tabpanel-1"
                />
              </Tabs>
            </Box>

            {/* Conteúdo das Abas */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ px: 4, pb: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center', 
                  mb: 3 
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Gerenciar Serviços
                  </Typography>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={openAddDialog}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                    }}
                  >
                    Novo Serviço
                  </Button>
                </Box>
                
                {/* Campo de pesquisa */}
                <SearchField 
                  searchTerm={searchTerm} 
                  setSearchTerm={setSearchTerm} 
                />
                
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 3, borderRadius: 2 }}
                    action={
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={fetchServicos}
                      >
                        Tentar Novamente
                      </Button>
                    }
                  >
                    {error}
                  </Alert>
                )}
                
                {/* Tabela de serviços */}
                <ServicosTable 
                  servicos={servicos}
                  loading={loading}
                  searchTerm={searchTerm}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  handleSort={handleSort}
                  openEditDialog={openEditDialog}
                  handleDeleteServico={handleDeleteServico}
                  handleToggleExibirNaEmissao={handleToggleExibirNaEmissao}
                  isDarkMode={isDarkMode}
                  customTheme={customTheme}
                />
              </Box>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Box sx={{ px: 4, pb: 4 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center', 
                  mb: 3 
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Gerenciar Motivos de Emissão
                  </Typography>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={openAddMotivoDialog}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                    }}
                  >
                    Novo Motivo
                  </Button>
                </Box>
                
                {/* Campo de pesquisa para motivos */}
                <SearchField 
                  searchTerm={searchTermMotivos} 
                  setSearchTerm={setSearchTermMotivos}
                  placeholder="Pesquisar motivos..."
                />
                
                {errorMotivos && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 3, borderRadius: 2 }}
                    action={
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={fetchMotivosEmissao}
                      >
                        Tentar Novamente
                      </Button>
                    }
                  >
                    {errorMotivos}
                  </Alert>
                )}
                
                {/* Tabela de motivos de emissão */}
                <MotivosTable 
                  motivos={motivosEmissao}
                  loading={loadingMotivos}
                  searchTerm={searchTermMotivos}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  handleSort={handleSort}
                  openEditDialog={openEditMotivoDialog}
                  handleDeleteMotivo={handleDeleteMotivo}
                  onToggleAtivo={handleToggleAtivoMotivo}
                  onToggleJustificativa={handleToggleJustificativaMotivo}
                  isDarkMode={isDarkMode}
                  customTheme={customTheme}
                />
              </Box>
            </TabPanel>
          </Paper>
        </Container>

        {/* Diálogo para adicionar/editar serviço */}
        <ServicoFormDialog 
          isOpen={dialogOpen}
          onClose={handleCloseDialog}
          isEditing={isEditing}
          currentServico={currentServico}
          formErrors={formErrors}
          submitting={submitting}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
        />

        {/* Modal de confirmação de exclusão de serviços */}
        <DeleteConfirmDialog 
          open={deleteConfirmDialog.open}
          servicoNome={deleteConfirmDialog.servicoNome}
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
        />

        {/* Diálogo para adicionar/editar motivo */}
        <MotivoFormDialog 
          isOpen={motivoDialogOpen}
          onClose={handleCloseMotivoDialog}
          isEditing={isEditingMotivo}
          currentMotivo={currentMotivo}
          formErrors={motivoFormErrors}
          submitting={submittingMotivo}
          handleInputChange={handleMotivoInputChange}
          handleSubmit={handleMotivoSubmit}
        />

        {/* Modal de confirmação de exclusão de motivos */}
        <DeleteConfirmDialog 
          open={deleteConfirmMotivoDialog.open}
          servicoNome={deleteConfirmMotivoDialog.motivoNome}
          onCancel={cancelDeleteMotivo}
          onConfirm={confirmDeleteMotivo}
          title="Confirmar Exclusão do Motivo"
          message="Tem certeza que deseja excluir este motivo de emissão?"
        />

        {/* Snackbar para mensagens */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ width: '100%', borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ServicoSenhas; 